import { merge } from 'lodash-es';

/**
 * A geocoding service is a service that converts a name to geo coordinates,
 * like address details, center point, bouding box, polygon, etc.
 *
 * This class is an abstraction layer to Google geocoding services. We could
 * use another geocoding service as long as the autocomplete and details methods
 * returns the same objects.
 */
export class GoogleGeocoding {
  /**
   * Google geo service instanciation.
   * @param input the input to autocomplete
   */
  constructor(input) {
    this.input = input;

    this.query = {
      types: [ 'geocode' ],
      componentRestrictions: { country: window.SEARCHABLE_COUNTRIES.split(',') },
    };

    // eslint-disable-next-line no-undef
    this.autocompleteService = new google.maps.places.AutocompleteService();
    // eslint-disable-next-line no-undef
    this.detailsService = new google.maps.places.PlacesService(this.input);
  }

  enablePlaces() {
    this.query.types = [ 'geocode', 'establishment' ];
  }

  /**
   * Auto completes the input. Returns a promise that resolves to an array of
   * predictions. (ex):
   *  [
   *    {
   *      id: "123456",
   *      name: "Paris, France"
   *    },
   *    {
   *      id: "4567789",
   *      name: "Perigeux, France"
   *    },
   *  ]
   *
   * @return {Promise}
   */
  autocomplete() {
    const mergedQuery = merge({ input: this.input.value }, this.query);

    return new Promise((resolve, reject) => {
      this.autocompleteService.getPlacePredictions(
        mergedQuery,
        (predictions, status) => {
          // eslint-disable-next-line no-undef
          if (status !== google.maps.places.PlacesServiceStatus.OK) {
            reject(status);
          } else {
            resolve(predictions.map((prediction) => ({
              id: prediction.place_id,
              name: prediction.description,
            })));
          }
        },
      );
    });
  }

  /**
   * Gets the details of a place found from the auto completion. Returns a object like :
   * {
   *  id: "123456",
   *  name: "Paris, France",
   *  viewport: {
   *    north: 1.0000,
   *    south: 1.0000,
   *    east: 1.0000,
   *    west: 1.0000,
   *  },
   *  location: {
   *    latitude: 1.0,
   *    longitude: 1.0,
   *  },
   *  address_components: {}
   * }
   * @param id
   * @return {Promise}
   */
  details(id) {
    return new Promise((resolve, reject) => {
      this.detailsService.getDetails({ placeId: id }, (place, status) => {
        // eslint-disable-next-line no-undef
        if (status !== google.maps.places.PlacesServiceStatus.OK) {
          reject(status);
        } else {
          const { viewport } = place.geometry;
          resolve({
            id,
            name: place.formatted_address,
            viewport: {
              north: viewport.getNorthEast().lat(),
              east: viewport.getNorthEast().lng(),
              south: viewport.getSouthWest().lat(),
              west: viewport.getSouthWest().lng(),
            },
            location: {
              latitude: place.geometry.location.lat(),
              longitude: place.geometry.location.lng(),
            },
            address_components: place.address_components,
            types: place.types,
          });
        }
      });
    });
  }
}
